'use client';

import { useEffect, useState } from 'react';
import { isNative } from '@/lib/native';

type Readout = Record<string, string>;

function px(n: number) {
  return `${Math.round(n)}px`;
}

function measure(): Readout {
  const root = document.documentElement;
  const cs = getComputedStyle(root);
  const probe = document.createElement('div');
  probe.style.cssText = 'position:fixed;top:0;left:0;width:0;height:0;padding-top:env(safe-area-inset-top);padding-bottom:env(safe-area-inset-bottom);visibility:hidden;pointer-events:none';
  document.body.appendChild(probe);
  const pcs = getComputedStyle(probe);
  const safeTop = pcs.paddingTop;
  const safeBottom = pcs.paddingBottom;
  probe.remove();
  const main = document.querySelector('main');
  const header = document.querySelector('header');
  const nav = document.querySelector('nav.app-bottom-nav');
  const sticky = document.querySelector('[role="tablist"]')?.parentElement ?? null;
  const vv = window.visualViewport;
  const r = (el: Element | null) => (el ? el.getBoundingClientRect() : null);
  const hr = r(header), mr = r(main), nr = r(nav), sr = r(sticky);
  const doc = document.scrollingElement!;
  return {
    ua: navigator.userAgent.replace(/^Mozilla\/5\.0 /, '').slice(0, 60),
    inner: `${window.innerWidth}×${window.innerHeight}`,
    'vv h/offTop/pageTop/scale': vv ? `${Math.round(vv.height)} / ${Math.round(vv.offsetTop)} / ${Math.round(vv.pageTop)} / ${vv.scale.toFixed(2)}` : 'n/a',
    'safe top/bottom': `${safeTop} / ${safeBottom}`,
    '--app-top-inset': cs.getPropertyValue('--app-top-inset').trim() || '-',
    'doc scrollTop/overflow': `${Math.round(doc.scrollTop)} / ${doc.scrollHeight - doc.clientHeight}`,
    'body h / html h': `${Math.round(document.body.getBoundingClientRect().height)} / ${Math.round(root.getBoundingClientRect().height)}`,
    header: hr ? `top ${px(hr.top)} h ${px(hr.height)} ${getComputedStyle(header!).position}` : 'none',
    main: mr && main ? `top ${px(mr.top)} bottom ${px(mr.bottom)} ovY ${getComputedStyle(main).overflowY} st ${Math.round(main.scrollTop)} padT ${getComputedStyle(main).paddingTop}` : 'none',
    'sticky bar': sr ? `top ${px(sr.top)} h ${px(sr.height)}` : 'none',
    'tab bar': nr ? `top ${px(nr.top)} bottom ${px(nr.bottom)}` : 'none',
    keyboard: `${root.getAttribute('data-keyboard') ?? 'closed'} ${cs.getPropertyValue('--keyboard-height').trim()}`,
  };
}

/**
 * Off in production unless it is deliberately switched on. The gesture is
 * three taps in the top 140px — exactly where a status bar, a Back button and
 * a top bar live — so on a shipped build anyone, an App Store reviewer
 * included, could summon a monospace diagnostics panel by accident. Turn it on
 * for a real device by running `localStorage.sweetspot_native_debug = '1'` in
 * the Safari inspector attached to the WKWebView, or by building a preview
 * with NEXT_PUBLIC_NATIVE_DEBUG=1. Development builds keep it always on.
 */
function debugEnabled(): boolean {
  if (process.env.NODE_ENV !== 'production') return true;
  if (process.env.NEXT_PUBLIC_NATIVE_DEBUG === '1') return true;
  try {
    return window.localStorage.getItem('sweetspot_native_debug') === '1';
  } catch {
    // Private mode / disabled storage throws on access; treat as off.
    return false;
  }
}

/**
 * Native-only diagnostics: triple-tap the very top of the screen (status bar
 * / top bar area) to toggle a readout of the numbers that decide layout in
 * the WKWebView — safe-area env(), visual viewport, document scroll, and the
 * boxes of header / main / sticky bar / tab bar. Exists because those values
 * cannot be observed from the Mac; a screenshot of this panel is the bug report.
 */
export function NativeDebugOverlay() {
  const [open, setOpen] = useState(false);
  const [armed, setArmed] = useState(false);
  const [data, setData] = useState<Readout>({});

  useEffect(() => {
    if (!isNative() || !debugEnabled()) return;
    setArmed(true);
    let taps: number[] = [];
    const onTouch = (e: TouchEvent) => {
      const t = e.changedTouches[0];
      if (!t || t.clientY > 140) return;
      const now = Date.now();
      taps = taps.filter((x) => now - x < 700);
      taps.push(now);
      if (taps.length >= 3) {
        taps = [];
        setOpen((o) => !o);
      }
    };
    document.addEventListener('touchend', onTouch, { passive: true });
    return () => document.removeEventListener('touchend', onTouch);
  }, []);

  useEffect(() => {
    if (!open) return;
    const tick = () => setData(measure());
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [open]);

  if (!open || !armed) return null;
  return (
    <div
      style={{
        position: 'fixed', left: 8, right: 8, bottom: 'calc(72px + env(safe-area-inset-bottom))', zIndex: 9999,
        background: 'rgba(20,18,9,.92)', color: '#F3EDE0', borderRadius: 10, padding: '10px 12px',
        font: '11px/1.45 ui-monospace, Menlo, monospace', pointerEvents: 'none', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
      }}
    >
      {Object.entries(data).map(([k, v]) => (
        <div key={k}><span style={{ color: '#FFC24D' }}>{k}</span>: {v}</div>
      ))}
    </div>
  );
}
